/**
 * Rotten Charm — Cart Drawer
 * Vanilla JS, no dependencies. Talks to Shopify's Ajax Cart API directly.
 *
 * Public contract for other sections (e.g. a future product-form.js):
 *   document.dispatchEvent(new CustomEvent('cart:open'))
 *   window.RottenCart.addItem(variantId, quantity) -> Promise<cart>
 *   document.addEventListener('cart:updated', (e) => e.detail /* full cart JSON *\/
 */
(function () {
  'use strict';

  const ENDPOINTS = {
    change: '/cart/change.js',
    add: '/cart/add.js',
    get: '/cart.js',
  };

  function trapFocus(container, closeFn) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function onKeydown(e) {
      if (e.key === 'Escape') {
        closeFn();
        return;
      }
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    first.focus();
    return () => container.removeEventListener('keydown', onKeydown);
  }

  class RottenCartDrawer {
    constructor(el) {
      this.el = el;
      this.releaseFocusTrap = null;
      this.lastFocusedTrigger = null;
      this.freeShippingThreshold = parseInt(el.dataset.freeShippingThreshold, 10) || 0;

      this.body = el.querySelector('[data-rc-cart-body]');
      this.itemsList = el.querySelector('[data-rc-cart-items]');
      this.footer = el.querySelector('[data-rc-cart-footer]');
      this.subtotalEl = el.querySelector('[data-rc-cart-subtotal]');
      this.shippingBar = el.querySelector('[data-rc-shipping-bar]');
      this.shippingMsg = el.querySelector('[data-rc-shipping-msg]');
      this.shippingFill = el.querySelector('[data-rc-shipping-fill]');

      this.bindOpenClose();
      this.bindItemInteractions();

      window.RottenCart = {
        addItem: (variantId, quantity) => this.addItem(variantId, quantity),
        open: () => this.open(),
      };
    }

    // ---- Open / close ----

    bindOpenClose() {
      document.addEventListener('cart:open', () => {
        this.lastFocusedTrigger = document.activeElement;
        this.open();
      });

      this.el.querySelectorAll('[data-rc-cart-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
    }

    open() {
      this.el.hidden = false;
      requestAnimationFrame(() => {
        this.el.dataset.open = 'true';
      });
      document.body.style.overflow = 'hidden';
      this.releaseFocusTrap = trapFocus(this.el, () => this.close());
    }

    close() {
      this.el.dataset.open = 'false';
      document.body.style.overflow = '';
      if (this.releaseFocusTrap) this.releaseFocusTrap();
      setTimeout(() => {
        this.el.hidden = true;
      }, 320);
      if (this.lastFocusedTrigger) this.lastFocusedTrigger.focus();
    }

    // ---- Ajax Cart API ----

    async addItem(variantId, quantity = 1) {
      try {
        const res = await fetch(ENDPOINTS.add, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
        });
        if (!res.ok) throw new Error('Add to cart failed');
        const cart = await this.refreshCart();
        this.open();
        return cart;
      } catch (err) {
        this.showError();
        throw err;
      }
    }

    async changeLine(lineKey, quantity) {
      const itemEl = this.itemsList?.querySelector(`[data-line-key="${lineKey}"]`);
      if (itemEl) itemEl.dataset.updating = 'true';

      try {
        const res = await fetch(ENDPOINTS.change, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity }),
        });
        if (!res.ok) throw new Error('Cart update failed');
        const cart = await res.json();
        this.render(cart);
        this.updateCount(cart);
        this.broadcast(cart);
        return cart;
      } catch (err) {
        if (itemEl) itemEl.dataset.updating = 'false';
        this.showError();
      }
    }

    // Fetch the latest cart JSON and re-render the drawer client-side.
    // (The drawer is a snippet in theme.liquid, not a section, so the
    // Section Rendering API can't be used to refresh it.)
    async refreshCart() {
      const res = await fetch(ENDPOINTS.get, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('Cart fetch failed');
      const cart = await res.json();
      this.render(cart);
      this.updateCount(cart);
      this.broadcast(cart);
      return cart;
    }

    updateCount(cart) {
      document.querySelectorAll('[data-rc-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('rc-header__cart-count--hidden', cart.item_count === 0);
      });
    }

    // Notify the rest of the theme (header badge, future sections) that the
    // cart changed. Dispatched as a bubbling document-level CustomEvent so
    // any listener can pick it up without a direct reference to this class.
    broadcast(cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    }

    showError() {
      if (!this.body) return;
      const existing = this.body.querySelector('[data-rc-cart-error]');
      if (existing) return;
      const msg = document.createElement('p');
      msg.setAttribute('data-rc-cart-error', '');
      msg.className = 'rc-cart-drawer__empty-body';
      msg.textContent = window.rcStrings?.cartError || 'Something went wrong. Try again.';
      this.body.prepend(msg);
      setTimeout(() => msg.remove(), 4000);
    }

    // ---- Rendering ----

    render(cart) {
      if (!this.body || !this.footer || !this.subtotalEl) return;

      if (cart.item_count === 0) {
        this.body.innerHTML = this.emptyStateHTML();
        this.itemsList = null;
        this.footer.hidden = true;
        if (this.shippingBar) this.shippingBar.hidden = true;
        return;
      }

      // The items list doesn't exist if the page loaded with an empty cart
      // (or the cart was emptied) — recreate it in place of the empty state.
      if (!this.itemsList || !this.body.contains(this.itemsList)) {
        this.body.innerHTML = '<ul class="rc-cart-drawer__items" data-rc-cart-items></ul>';
        this.itemsList = this.body.querySelector('[data-rc-cart-items]');
      }

      this.footer.hidden = false;
      this.subtotalEl.textContent = this.formatMoney(cart.total_price);

      // Re-render every line item. For typical drawer sizes (a handful of
      // items) a full re-render is simpler and safer than a diff, and it
      // guarantees the DOM always matches server state exactly.
      this.itemsList.innerHTML = cart.items.map((item) => this.lineItemHTML(item)).join('');
      this.updateShippingBar(cart.total_price);
    }

    updateShippingBar(totalPriceCents) {
      if (!this.shippingBar || !this.freeShippingThreshold) return;
      this.shippingBar.hidden = false;
      const remaining = this.freeShippingThreshold - totalPriceCents;

      if (remaining <= 0) {
        this.shippingMsg.textContent = window.rcStrings?.freeShippingUnlocked || 'Free shipping unlocked';
      } else {
        const amount = this.formatMoney(remaining);
        const template = window.rcStrings?.freeShippingProgress || "You're {{ amount }} away from free shipping";
        this.shippingMsg.textContent = template.replace('{{ amount }}', amount);
      }

      const pct = Math.min(100, (totalPriceCents / this.freeShippingThreshold) * 100);
      this.shippingFill.style.width = pct + '%';
    }

    lineItemHTML(item) {
      return `
        <li class="rc-cart-item" data-rc-cart-item data-line-key="${item.key}">
          <a href="${item.url}" class="rc-cart-item__image-link" tabindex="-1" aria-hidden="true">
            ${item.image ? `<img class="rc-cart-item__image" src="${this.imageUrl(item.image, 160)}" width="80" height="100" alt="" loading="lazy">` : ''}
          </a>
          <div class="rc-cart-item__details">
            <a href="${item.url}" class="rc-cart-item__title">${item.product_title}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="rc-cart-item__variant">${item.variant_title}</p>` : ''}
            <div class="rc-cart-item__row">
              <div class="rc-cart-item__stepper" data-rc-qty-stepper>
                <button type="button" class="rc-cart-item__qty-btn" data-rc-qty-decrease aria-label="Decrease quantity">−</button>
                <span class="rc-cart-item__qty-value" data-rc-qty-value aria-live="polite">${item.quantity}</span>
                <button type="button" class="rc-cart-item__qty-btn" data-rc-qty-increase aria-label="Increase quantity">+</button>
              </div>
              <span class="rc-cart-item__price" data-rc-line-price>${this.formatMoney(item.final_line_price)}</span>
            </div>
          </div>
          <button type="button" class="rc-cart-item__remove" data-rc-cart-remove aria-label="Remove ${item.product_title}">×</button>
        </li>
      `;
    }

    emptyStateHTML() {
      return `
        <div class="rc-cart-drawer__empty">
          <p class="rc-cart-drawer__empty-title">${window.rcStrings?.emptyTitle || 'Your Queue is Empty'}</p>
          <p class="rc-cart-drawer__empty-body">${window.rcStrings?.emptyBody || 'No screenings selected yet. Fix that.'}</p>
          <a href="/collections/all" class="rc-cart-drawer__continue-btn" data-rc-cart-close>
            ${window.rcStrings?.continueShopping || 'Browse the Catalog'}
          </a>
        </div>
      `;
    }

    imageUrl(src, width) {
      // Shopify CDN URLs accept a `width` query param directly.
      return src.replace(/(\.[a-z]+)(\?|$)/i, `_${width}x$1$2`);
    }

    formatMoney(cents) {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      });
    }

    // ---- Delegated item interactions (qty +/-, remove) ----
    // Bound on the drawer root so it keeps working after the items list is
    // rebuilt by render().

    bindItemInteractions() {
      this.el.addEventListener('click', (e) => {
        const itemEl = e.target.closest('[data-rc-cart-item]');
        if (!itemEl) return;
        const lineKey = itemEl.dataset.lineKey;
        const qtyEl = itemEl.querySelector('[data-rc-qty-value]');
        const currentQty = parseInt(qtyEl?.textContent || '0', 10);

        if (e.target.closest('[data-rc-qty-increase]')) {
          this.changeLine(lineKey, currentQty + 1);
        } else if (e.target.closest('[data-rc-qty-decrease]')) {
          this.changeLine(lineKey, Math.max(0, currentQty - 1));
        } else if (e.target.closest('[data-rc-cart-remove]')) {
          this.changeLine(lineKey, 0);
        }
      });
    }
  }

  document.querySelectorAll('[data-rc-cart-drawer]').forEach((el) => new RottenCartDrawer(el));
})();
